import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useUpdateStoreOrderStatusMutation } from "@/modules/stores/services/mutations/useUpdateStoreOrderStatusMutation";
import { useStoreOrderByIdQuery } from "@/modules/stores/services/queries/useStoreOrderByIdQuery";
import { useStoreOrderProofQuery } from "@/modules/stores/services/queries/useStoreOrderProofQuery";
import { ArrowLeft } from "lucide-react";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import { usePageTitle } from "@/shared/layout/page-title";
import { Button, ErrorState, LoadingState } from "@/shared/rd";
import { OrderDetails } from "@/modules/stores/components/orders/OrderDetails";
import { getQueryErrorStatus } from "@/lib/query/core/get-query-error-status";

type StoreOrderPageProps = {
  locale: string;
  storeId: string;
  orderId: string;
};

export function StoreOrderPage({
  locale,
  storeId,
  orderId,
}: StoreOrderPageProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const { mutateAsync: updateOrderStatus } = useUpdateStoreOrderStatusMutation();
  const { data: order, isLoading, error, refetch } = useStoreOrderByIdQuery({
    storeId,
    orderId,
  });
  const {
    data: proofData,
    isLoading: isProofLoading,
    error: proofQueryError,
  } = useStoreOrderProofQuery({
    storeId,
    orderId,
  });
  const guard = useNavigationGuard();
  const orderErrorStatus = getQueryErrorStatus(error);
  // The bar names THIS order: its code, then the buyer and their phone.
  usePageTitle(order?.orderCode, order?.customer.name, order?.customer.phoneE164);
  const isProofNotFound =
    proofQueryError instanceof ApiJsonClientError && proofQueryError.status === 404;
  const proofItems = isProofNotFound ? [] : (proofData?.items ?? []);
  const proofError = Boolean(proofQueryError) && !isProofNotFound;

  useEffect(() => {
    if (orderErrorStatus === 404) {
      navigate(`/${locale}/admin/stores/${storeId}`, { replace: true });
    }
  }, [locale, orderErrorStatus, navigate, storeId]);

  if (isLoading) {
    return <LoadingState title={t(keys.shell.loadingTitle, { page: t("admin.orderStatePage") })} sub={t(keys.shell.loadingSub)} rows={4} />;
  }

  if (orderErrorStatus === 404) {
    return null;
  }

  if (error || !order) {
    return (
      <ErrorState
        title={t(keys.shell.errorTitle, { page: t("admin.orderStatePage") })}
        sub={t(keys.shell.errorSub)}
        onRetry={() => void refetch()}
        retryLabel={t(keys.shell.retry)}
      />
    );
  }

  const fallbackHref = `/${locale}/admin/stores/${storeId}`;
  const navigateBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate(fallbackHref);
  };

  return (
    <>
      <div>
        <Button
          variant="ghost"
          size="sm"
          muted
          icon={ArrowLeft}
          className="rd-flip-rtl"
          onClick={() => {
            if (guard?.isDirty) {
              guard.promptDiscard(() => navigateBack(), { allowHistoryPop: true });
              return;
            }
            navigateBack();
          }}
        >
          {t(keys.common.back)}
        </Button>
      </div>

      <OrderDetails
        locale={locale}
        storeId={storeId}
        order={order}
        proofState={{
          items: proofItems,
          isLoading: isProofLoading,
          isError: proofError,
        }}
        onUpdateStatus={(payload) =>
          updateOrderStatus({
            storeId,
            orderId,
            ...payload,
          })
        }
      />
    </>
  );
}
